(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktopQuery = window.matchMedia('(min-width: 750px)');
  const MIN_RING_DURATION = 400;

  const isDesktop = () => desktopQuery.matches;

  const scopeOf = (video) => video?.dataset?.heroScope || null;

  const matchesViewport = (video) => {
    const scope = scopeOf(video);
    if (scope === 'desktop') return isDesktop();
    if (scope === 'mobile') return !isDesktop();
    return video.offsetParent !== null;
  };

  const attachSrc = (video) => {
    const src = video.dataset.heroSrc;
    if (!src || video.getAttribute('src') === src) return;
    video.src = src;
    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;
  };

  const detachSrc = (video) => {
    if (!video.getAttribute('src') && !video.currentSrc) return;
    video.pause();
    video.removeAttribute('src');
    video.preload = 'none';
    video.load();
  };

  /* Only the matching breakpoint video gets a src. The other one stays empty
     so it never appears in the network panel. */
  const bindBreakpointVideos = () => {
    document.querySelectorAll('[data-hero-src]').forEach((video) => {
      if (matchesViewport(video)) {
        attachSrc(video);
      } else {
        detachSrc(video);
      }
    });
  };

  const setupSlider = (sliderEl) => {
    const swiper = sliderEl.slider;
    if (!swiper) return false;
    if (sliderEl.dataset.cosmicHeroReady === 'true') return true;
    sliderEl.dataset.cosmicHeroReady = 'true';

    const autoplayEnabled = sliderEl.dataset.autoplay === 'true';
    const autoplayDelay = Number(sliderEl.dataset.autoplayInterval) || 6000;
    const holdForVideo =
      sliderEl.dataset.advanceOnVideoEnd === 'true' && !reducedMotion.matches;
    const slides = Array.from(sliderEl.querySelectorAll('.hero__swiper-slide'));

    let pausedByUser = false;
    let safetyTimer = null;

    const bullets = () => sliderEl.querySelectorAll('.swiper-pagination-bullet');

    const videoIn = (slide) => {
      if (!slide) return null;
      return (
        Array.from(slide.querySelectorAll('video')).find((video) => matchesViewport(video)) ||
        null
      );
    };

    const markPaused = (isPaused) => {
      sliderEl.dataset.slideshowPaused = isPaused ? 'true' : 'false';
      sliderEl.querySelectorAll('.hero__dot--video').forEach((dot) => {
        dot.setAttribute('aria-label', isPaused ? 'Videoyu oynat' : 'Videoyu duraklat');
      });
    };

    const syncRingDurations = () => {
      const dots = bullets();
      slides.forEach((slide, index) => {
        const dot = dots[index];
        if (!dot) return;
        const video = holdForVideo ? videoIn(slide) : null;
        const duration =
          video && Number.isFinite(video.duration) && video.duration > 0
            ? video.duration * 1000
            : autoplayDelay;
        dot.style.setProperty(
          '--hero-dot-duration',
          `${Math.max(Math.round(duration), MIN_RING_DURATION)}ms`
        );
      });
    };

    const stopSafetyTimer = () => {
      if (safetyTimer) {
        clearTimeout(safetyTimer);
        safetyTimer = null;
      }
    };

    const advance = (fromIndex) => {
      stopSafetyTimer();
      if (pausedByUser || swiper.activeIndex !== fromIndex) return;
      swiper.slideNext();
    };

    const scheduleSafetyAdvance = (video, index, delay) => {
      stopSafetyTimer();
      safetyTimer = setTimeout(() => {
        safetyTimer = null;
        if (pausedByUser) return;
        const remaining = (video.duration - video.currentTime) * 1000;
        if (!video.paused && Number.isFinite(remaining) && remaining > 250) {
          scheduleSafetyAdvance(video, index, remaining + 1000);
          return;
        }
        advance(index);
      }, delay);
    };

    const enterVideoSlide = (video, index, { restart = true } = {}) => {
      swiper.autoplay?.stop();
      attachSrc(video);
      if (restart || video.ended) {
        video.currentTime = 0;
      }

      const playback = video.play();
      if (playback && typeof playback.then === 'function') {
        playback.catch(() => advance(index));
      }

      const armSafety = () => {
        const remaining =
          Number.isFinite(video.duration) && video.duration > 0
            ? Math.max(video.duration - video.currentTime, 0) * 1000
            : autoplayDelay;
        syncRingDurations();
        scheduleSafetyAdvance(video, index, remaining + 2000);
      };

      if (video.readyState >= 1) {
        armSafety();
      } else {
        video.addEventListener('loadedmetadata', armSafety, { once: true });
      }

      setTimeout(() => {
        if (video.paused && !pausedByUser && swiper.activeIndex === index) advance(index);
      }, 2500);
    };

    const enterImageSlide = () => {
      stopSafetyTimer();
      slides.forEach((slide) => {
        const video = videoIn(slide);
        if (video) video.pause();
      });
      if (autoplayEnabled && !pausedByUser) swiper.autoplay?.start();
    };

    const handleSlide = () => {
      const index = swiper.activeIndex;
      const video = videoIn(slides[index]);

      if (video && holdForVideo) {
        enterVideoSlide(video, index);
      } else {
        enterImageSlide();
      }
    };

    slides.forEach((slide) => {
      slide.querySelectorAll('video').forEach((video) => {
        video.addEventListener('loadedmetadata', syncRingDurations);
        video.addEventListener('ended', () => {
          const index = slides.indexOf(slide);
          advance(index);
        });
        video.addEventListener('pause', () => {
          if (pausedByUser || video.ended) return;
          if (!matchesViewport(video)) return;
          if (slides.indexOf(slide) !== swiper.activeIndex) return;
          video.play().catch(() => {});
        });
        video.addEventListener('play', () => {
          if (matchesViewport(video)) return;
          video.pause();
        });
      });
    });

    swiper.on('slideChange', handleSlide);

    const pauseVideo = (video) => {
      pausedByUser = true;
      stopSafetyTimer();
      swiper.autoplay?.stop();
      markPaused(true);
      video.pause();
    };

    const resumeVideo = (video, index) => {
      pausedByUser = false;
      markPaused(false);
      enterVideoSlide(video, index, { restart: false });
    };

    const bindVideoDots = () => {
      const dots = bullets();
      slides.forEach((slide, index) => {
        const video = videoIn(slide);
        const dot = dots[index];
        if (!video || !dot || dot.dataset.heroVideoDot === 'true') return;
        dot.dataset.heroVideoDot = 'true';
        dot.classList.add('hero__dot--video');
        if (!dot.querySelector('.hero__dot-control')) {
          const control = document.createElement('i');
          control.className = 'hero__dot-control';
          control.setAttribute('aria-hidden', 'true');
          dot.appendChild(control);
        }
        dot.setAttribute('aria-label', 'Videoyu duraklat');
        dot.addEventListener(
          'click',
          (event) => {
            if (swiper.activeIndex !== index) return;
            event.preventDefault();
            event.stopPropagation();
            if (pausedByUser || video.paused) {
              resumeVideo(video, index);
            } else {
              pauseVideo(video);
            }
          },
          true
        );
      });
    };

    sliderEl.cosmicHeroPause = () => {
      const video = videoIn(slides[swiper.activeIndex]);
      if (video) pauseVideo(video);
    };

    sliderEl.cosmicHeroResume = () => {
      const index = swiper.activeIndex;
      const video = videoIn(slides[index]);
      if (video) resumeVideo(video, index);
    };

    markPaused(false);
    syncRingDurations();
    bindVideoDots();
    handleSlide();

    desktopQuery.addEventListener('change', () => {
      bindBreakpointVideos();
      handleSlide();
    });

    return true;
  };

  const boot = () => {
    bindBreakpointVideos();
    document.querySelectorAll('hero-slider').forEach((sliderEl) => setupSlider(sliderEl));
  };

  const bootWithRetries = () => {
    let attempts = 0;
    const tick = () => {
      boot();
      const pending = Array.from(document.querySelectorAll('hero-slider')).some(
        (sliderEl) => sliderEl.dataset.cosmicHeroReady !== 'true'
      );
      if (pending && attempts < 40) {
        attempts += 1;
        setTimeout(tick, 100);
      }
    };
    tick();
  };

  bindBreakpointVideos();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootWithRetries);
  } else {
    bootWithRetries();
  }

  document.addEventListener('hero-slider:mounted', boot);
  document.addEventListener('shopify:section:load', bootWithRetries);
})();
